import { useCallback, useRef, useState } from "react";
import useWebSocket, { ReadyState } from "react-use-websocket";

import { v4 as uuidv4 } from "uuid";
import { IButton, IMessage } from "../domain/message.interface";
import { WS_CHAT_URL } from "../infra/settings";
import {
  WebSocketPayload,
  WebSocketPayloadButton,
} from "../domain/webSocketPayload.interface";

const threadId = (() => {
  let id = localStorage.getItem("threadId");
  if (!id) {
    id = uuidv4();
    localStorage.setItem("threadId", id);
  }
  return id;
})();

const saveMessageInLocalStorage = (message: IMessage) => {
  let storageMessages = localStorage.getItem("messages");
  let messages = [];

  if (storageMessages) {
    messages = JSON.parse(storageMessages);
  }

  messages.push(message);

  localStorage.setItem("messages", JSON.stringify(messages));
};

const loadMessagesFromLocalStorage = () => {
  let messages = localStorage.getItem("messages");
  if (!messages) {
    messages = JSON.stringify([]);
  }
  return JSON.parse(messages);
};

const useChatInteractions = () => {
  const initMessages = loadMessagesFromLocalStorage();

  const [messages, setMessages] = useState<IMessage[]>(initMessages);
  const [buttons, setButtons] = useState<string[]>([]);
  const [enabledInput, setEnabledInput] = useState(true);
  const [isTyping, setIsTyping] = useState(false);
  const typingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const appendMessages = useCallback(
    (
      newMessages: IMessage[],
    ) => {
      for (const message of newMessages) {
        saveMessageInLocalStorage(message);
      }

      setMessages([
        ...messages,
        ...newMessages,
      ]);
    },
    [messages],
  );

  const { sendMessage, readyState } = useWebSocket(
    WS_CHAT_URL + "/websocket/" + threadId,
    {
      shouldReconnect: () => true,
      reconnectAttempts: 1000,
      reconnectInterval: 10,
      onMessage: (event) => onMessageEvent(event),
    },
  );

  const convertButtonsFromWebsocket = (
    websocketButtons: WebSocketPayloadButton[],
  ): IButton[] => {
    return websocketButtons.map((button) => ({
      subType: button.sub_type,
      options: button.options.map((option) => ({
        id: option.id,
        label: option.label,
      })),
    }));
  };

  const onMessageEvent = (event: WebSocketEventMap["message"]) => {
    if (readyState === ReadyState.OPEN) {
      setIsTyping(false);
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
        typingTimeoutRef.current = null;
      }
      const {
        error,
        message,
        buttons,
      } = JSON
        .parse(event.data) as WebSocketPayload;
      if (!error) {
        appendMessages([
          buildMessage(message, false, convertButtonsFromWebsocket(buttons)),
        ]);
      } else {
        appendMessages([
          buildMessage("Desculpe, não consegui entender.", false, []),
        ]);
      }
    }
  };

  const buildMessage = (
    message: string,
    userMessage: boolean,
    buttons: IButton[],
  ): IMessage => {
    return {
      userMessage,
      content: message,
      buttons: buttons || [],
    };
  };

  const sendMessageToServer = (message: string): void => {
    sendMessage(
      JSON.stringify({
        message,
        threadId,
        source: "becker",
        userIdentifier: "notused",
      }),
    );
  };

  const handleChatSubmit = (message: string): void => {
    selectButtons([]);
    sendMessageToServer(message);
    appendMessages([buildMessage(message, true, [])]);
    setIsTyping(true);
    typingTimeoutRef.current = setTimeout(() => setIsTyping(false), 30000);
  };

  const selectButtons = (buttons: string[]): void => {
    setButtons(buttons);
  };

  return {
    messages,
    buttons,
    enabledInput,
    isTyping,
    handleChatSubmit,
    selectButtons,
    setEnabledInput,
  };
};

export default useChatInteractions;
export type { IMessage };
